import { useEffect, useState } from 'react';
import type { SymbolMap } from '@mtg-deck-tool/shared';
import { api } from '../lib/api-client.js';

const STATIC_SYMBOLS: SymbolMap = {
  '{W}': 'https://svgs.scryfall.io/card-symbols/W.svg',
  '{U}': 'https://svgs.scryfall.io/card-symbols/U.svg',
  '{B}': 'https://svgs.scryfall.io/card-symbols/B.svg',
  '{R}': 'https://svgs.scryfall.io/card-symbols/R.svg',
  '{G}': 'https://svgs.scryfall.io/card-symbols/G.svg',
  '{C}': 'https://svgs.scryfall.io/card-symbols/C.svg',
  '{T}': 'https://svgs.scryfall.io/card-symbols/T.svg',
  '{X}': 'https://svgs.scryfall.io/card-symbols/X.svg',
  '{0}': 'https://svgs.scryfall.io/card-symbols/0.svg',
  '{1}': 'https://svgs.scryfall.io/card-symbols/1.svg',
  '{2}': 'https://svgs.scryfall.io/card-symbols/2.svg',
  '{3}': 'https://svgs.scryfall.io/card-symbols/3.svg',
  '{4}': 'https://svgs.scryfall.io/card-symbols/4.svg',
  '{5}': 'https://svgs.scryfall.io/card-symbols/5.svg',
  '{6}': 'https://svgs.scryfall.io/card-symbols/6.svg',
  '{7}': 'https://svgs.scryfall.io/card-symbols/7.svg',
  '{8}': 'https://svgs.scryfall.io/card-symbols/8.svg',
  '{9}': 'https://svgs.scryfall.io/card-symbols/9.svg',
};

// Module-level cache — fetched once per page load
let _cache: SymbolMap | null = { ...STATIC_SYMBOLS };
let _promise: Promise<SymbolMap> | null = null;

export function useSymbolMap(): SymbolMap {
  const [map, setMap] = useState<SymbolMap>(_cache ?? { ...STATIC_SYMBOLS });

  useEffect(() => {
    // If we've already loaded the full scryfall map beyond static defaults, skip API
    if (_cache && Object.keys(_cache).length > Object.keys(STATIC_SYMBOLS).length) {
      setMap(_cache);
      return;
    }
    if (!_promise) {
      _promise = api.getSymbols().then((result) => {
        _cache = { ...STATIC_SYMBOLS, ...result };
        return _cache;
      });
    }
    _promise
      .then((result) => setMap(result))
      .catch(() => {
        // symbol map API failed/slow — components fall back to static or Proxy resolution
      });
  }, []);

  return new Proxy(map, {
    get(target, prop) {
      if (typeof prop === 'string') {
        if (target[prop] !== undefined) {
          return target[prop];
        }
        // Dynamic fallback builder for any standard format like {W}, {2}, {U/B}, etc.
        if (prop.startsWith('{') && prop.endsWith('}')) {
          const inner = prop.slice(1, -1).replace('/', '');
          return `https://svgs.scryfall.io/card-symbols/${inner}.svg`;
        }
      }
      return target[prop as keyof typeof target];
    }
  });
}
